class OppcolorTheme {
  constructor() {
    this.routes = window.routes || {};
    this.strings = window.themeStrings || {};
    this.bind();
    this.refreshCart();
  }

  bind() {
    this.setupNavigation();
    this.setupAnnouncementSlider();
    this.setupPurchaseEntrySliders();
    this.setupPanels();
    this.setupCollectionFilters();
    this.setupProductForms();
    this.setupVariantPickers();
    this.setupProductQuickAdd();
    this.setupProductGallery();
    this.setupRelatedProducts();
    this.setupSizeChart();
    this.setupNewsletterForms();
    this.setupHeaderScrollState();
    this.setupBackToTop();
    this.setupYoutubeVideos();
    this.setupThemeEditor();
    this.setupPopup();
  }

  setupNavigation() {
    const megaItems = [...document.querySelectorAll('.has-mega')];
    const nav = document.querySelector('.nav');
    if (!document.querySelector('.mega-scrim')) {
      const scrim = document.createElement('div');
      scrim.className = 'mega-scrim';
      scrim.setAttribute('aria-hidden', 'true');
      document.body.appendChild(scrim);
    }
    const updateScrimTop = () => {
      if (!nav) return;
      document.documentElement.style.setProperty('--mega-scrim-top', `${Math.round(nav.getBoundingClientRect().bottom)}px`);
    };
    const sync = () => {
      updateScrimTop();
      document.body.classList.toggle('mega-active', megaItems.some((item) => item.matches(':hover, :focus-within') || item.classList.contains('mega-open')));
    };
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', updateScrimTop, { passive: true });
    megaItems.forEach((item) => {
      item.addEventListener('mouseenter', () => { updateScrimTop(); document.body.classList.add('mega-active'); });
      item.addEventListener('mouseleave', () => requestAnimationFrame(sync));
      item.addEventListener('focusin', () => { updateScrimTop(); document.body.classList.add('mega-active'); });
      item.addEventListener('focusout', () => requestAnimationFrame(sync));
    });
    document.querySelector('[data-menu-toggle]')?.addEventListener('click', (event) => {
      const navCenter = document.querySelector('[data-nav-center]');
      const open = navCenter?.classList.toggle('open');
      event.currentTarget.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('mobile-menu-open', Boolean(open));
      sync();
    });
    document.querySelector('.mega-scrim')?.addEventListener('click', () => {
      const navCenter = document.querySelector('[data-nav-center]');
      const menuToggle = document.querySelector('[data-menu-toggle]');
      navCenter?.classList.remove('open');
      menuToggle?.setAttribute('aria-expanded', 'false');
      megaItems.forEach((item) => item.classList.remove('mega-open'));
      document.body.classList.remove('mobile-menu-open');
      sync();
    });
    document.querySelectorAll('[data-mega-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        button.closest('.nav-item')?.classList.toggle('mega-open');
        sync();
      });
    });
  }

  setupAnnouncementSlider() {
    document.querySelectorAll('[data-announcement-slider]').forEach((bar) => {
      const slider = bar.querySelector('.announcement-swiper');
      const slides = bar.querySelectorAll('.swiper-slide');
      if (!slider || slides.length < 2 || typeof Swiper === 'undefined') return;
      const transitionEffect = bar.dataset.effect || 'slide';
      const isFade = transitionEffect === 'fade';
      const isVertical = transitionEffect === 'vertical';
      const instance = new Swiper(slider, {
        loop: true,
        direction: isVertical ? 'vertical' : 'horizontal',
        effect: isFade ? 'fade' : 'slide',
        fadeEffect: {
          crossFade: true
        },
        speed: 520,
        allowTouchMove: true,
        autoplay: bar.dataset.autoplay === 'true'
          ? {
              delay: Number(bar.dataset.delay || 4000),
              disableOnInteraction: false
            }
          : false
      });
      if (bar.dataset.autoplay === 'true') {
        bar.addEventListener('mouseenter', () => instance.autoplay?.stop());
        bar.addEventListener('mouseleave', () => instance.autoplay?.start());
      }
    });
  }

  setupPurchaseEntrySliders() {
    document.querySelectorAll('[data-purchase-entry]').forEach((wrap) => {
      const slider = wrap.querySelector('[data-purchase-entry-slider]');
      const slides = wrap.querySelectorAll('.swiper-slide');
      if (!slider || slides.length < 2 || typeof Swiper === 'undefined') return;

      const mobileSlides = Number(wrap.dataset.mobileSlides || 2.2);
      const desktopSlides = Number(wrap.dataset.desktopSlides || 3);
      const mobileGroup = Number(wrap.dataset.mobileGroup || 1);
      const desktopGroup = Number(wrap.dataset.desktopGroup || 1);
      const mobileArrows = wrap.dataset.mobileArrows === 'true';
      const desktopArrows = wrap.dataset.desktopArrows === 'true';
      const mobilePagination = wrap.dataset.mobilePagination === 'true';
      const desktopPagination = wrap.dataset.desktopPagination === 'true';
      const prevButton = wrap.querySelector('[data-purchase-entry-prev]');
      const nextButton = wrap.querySelector('[data-purchase-entry-next]');
      const pagination = wrap.querySelector('[data-purchase-entry-pagination]');
      const media = window.matchMedia('(min-width: 1081px)');

      const syncControls = () => {
        const isDesktop = media.matches;
        wrap.classList.toggle('entry-slider--show-arrows', isDesktop ? desktopArrows : mobileArrows);
        wrap.classList.toggle('entry-slider--show-pagination', isDesktop ? desktopPagination : mobilePagination);
      };

      syncControls();
      media.addEventListener?.('change', syncControls);

      new Swiper(slider, {
        speed: 520,
        spaceBetween: 12,
        allowTouchMove: true,
        grabCursor: true,
        simulateTouch: true,
        threshold: 4,
        touchRatio: 1,
        slidesPerView: mobileSlides,
        slidesPerGroup: mobileGroup,
        watchOverflow: true,
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton
        },
        pagination: {
          el: pagination,
          clickable: true
        },
        breakpoints: {
          1081: {
            spaceBetween: 14,
            slidesPerView: desktopSlides,
            slidesPerGroup: desktopGroup
          }
        }
      });
    });
  }

  setupPanels() {
    document.querySelectorAll('[data-open-search]').forEach((button) => {
      button.addEventListener('click', () => {
        this.openPanel('.search-panel');
        setTimeout(() => document.querySelector('[data-search-input]')?.focus(), 100);
      });
    });
    document.querySelectorAll('[data-open-cart]').forEach((button) => {
      button.addEventListener('click', () => this.openCartPanel());
    });
    document.querySelectorAll('[data-close-panel], .overlay').forEach((button) => {
      button.addEventListener('click', () => this.closePanels());
    });
    document.querySelectorAll('[data-close-filter]').forEach((button) => {
      button.addEventListener('click', () => this.closePanels());
    });
    document.querySelector('[data-search-input]')?.addEventListener('input', (event) => this.predictiveSearch(event.target.value));
    document.querySelector('[data-cart-items]')?.addEventListener('click', (event) => {
      const control = event.target.closest('[data-cart-key]');
      if (!control) return;
      this.changeCart(control.dataset.cartKey, Number(control.dataset.cartQuantity));
    });
  }

  setupCollectionFilters() {
    document.querySelectorAll('[data-filter-toggle]').forEach((button) => {
      if (button.dataset.filterToggleBound === 'true') return;
      button.dataset.filterToggleBound = 'true';
      button.addEventListener('click', () => {
        const layout = button.closest('.collection-layout');
        const open = !layout?.classList.contains('filters-open');
        layout?.classList.toggle('filters-open', open);
        button.setAttribute('aria-expanded', String(open));
        document.querySelector('.overlay')?.classList.toggle('open', Boolean(open));
        document.documentElement.classList.toggle('filter-lock', Boolean(open));
        document.body.classList.toggle('filter-lock', Boolean(open));
      });
    });
    document.querySelectorAll('[data-close-filter]').forEach((button) => {
      if (button.dataset.filterCloseBound === 'true') return;
      button.dataset.filterCloseBound = 'true';
      button.addEventListener('click', () => this.closePanels());
    });
    document.querySelectorAll('[data-price-range]').forEach((range) => {
      if (range.dataset.priceRangeBound === 'true') return;
      range.dataset.priceRangeBound = 'true';
      this.setupPriceRange(range);
    });
    document.querySelectorAll('[data-collection-section]').forEach((section) => {
      if (section.dataset.collectionAjaxBound === 'true') return;
      section.dataset.collectionAjaxBound = 'true';
      section.addEventListener('submit', (event) => {
        const form = event.target.closest('#FacetFiltersForm, #SortByForm');
        if (!form || !section.contains(form)) return;
        event.preventDefault();
        this.renderCollection(this.collectionUrlFromForm(form, section), {
          closeFilters: form.id === 'FacetFiltersForm'
        });
      });
      section.addEventListener('click', (event) => {
        const link = event.target.closest('.active-filters a, .filter-summary a, .pagination a');
        if (!link || !section.contains(link)) return;
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        event.preventDefault();
        this.renderCollection(url.href, {
          scrollToGrid: Boolean(link.closest('.pagination')),
          closeFilters: true
        });
      });
    });
    if (!this.collectionPopstateBound) {
      this.collectionPopstateBound = true;
      window.addEventListener('popstate', () => {
        if (document.querySelector('[data-collection-section]')) {
          this.renderCollection(window.location.href, { pushState: false });
        }
      });
    }
  }

  collectionUrlFromForm(form, section) {
    const baseUrl = new URL(section.dataset.collectionUrl || window.location.pathname, window.location.origin);
    const params = form.id === 'SortByForm'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams(new FormData(form));

    params.delete('page');
    params.delete('section_id');

    if (form.id === 'SortByForm') {
      const sortValue = form.querySelector('[name="sort_by"]')?.value;
      if (sortValue) params.set('sort_by', sortValue);
    } else {
      const sortValue = section.querySelector('#SortBy')?.value;
      if (sortValue) params.set('sort_by', sortValue);
    }

    [...params.entries()].forEach(([key, value]) => {
      if (value === '') params.delete(key);
    });

    const query = params.toString();
    return `${baseUrl.pathname}${query ? `?${query}` : ''}`;
  }

  async renderCollection(url, options = {}) {
    const currentSection = document.querySelector('[data-collection-section]');
    if (!currentSection) {
      window.location.href = url;
      return;
    }

    const sectionId = currentSection.dataset.sectionId;
    const historyUrl = new URL(url, window.location.origin);
    historyUrl.searchParams.delete('section_id');
    const requestUrl = new URL(historyUrl.href);
    requestUrl.searchParams.set('section_id', sectionId);

    currentSection.classList.add('is-loading');
    try {
      const response = await fetch(requestUrl.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Collection update failed');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = doc.querySelector(`[data-collection-section][data-section-id="${sectionId}"]`) || doc.querySelector('[data-collection-section]');
      if (!nextSection) throw new Error('Collection section missing');

      currentSection.replaceWith(nextSection);
      this.setupCollectionFilters();
      if (options.closeFilters) this.closePanels();
      if (options.pushState !== false) window.history.pushState({}, '', historyUrl.href);
      if (options.scrollToGrid) {
        nextSection.querySelector('.collection-main')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (_) {
      window.location.href = historyUrl.href;
    }
  }

  setupPriceRange(range) {
    const minRange = range.querySelector('[data-price-min-range]');
    const maxRange = range.querySelector('[data-price-max-range]');
    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');
    const minDisplay = range.querySelector('[data-price-min-display]');
    const maxDisplay = range.querySelector('[data-price-max-display]');
    const slider = range.querySelector('[data-price-track]');
    if (!minRange || !maxRange || !minInput || !maxInput || !slider) return;

    const floor = Number(minRange.min || 0);
    const ceiling = Number(maxRange.max || 0);
    const clamp = (value, min, max) => Math.min(Math.max(Number(value) || 0, min), max);
    const currency = range.dataset.currency || 'USD';
    const cleanValue = (value) => {
      const rounded = Math.round(Number(value) * 100) / 100;
      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    };
    const renderMoney = (value) => this.money(Math.round(Number(value) * 100), currency);

    const sync = (activeRange) => {
      let min = clamp(minRange.value, floor, ceiling);
      let max = clamp(maxRange.value, floor, ceiling);

      if (min > max) {
        if (activeRange === maxRange) {
          min = max;
          minRange.value = String(min);
        } else {
          max = min;
          maxRange.value = String(max);
        }
      }

      minInput.value = min > floor ? cleanValue(min) : '';
      maxInput.value = max < ceiling ? cleanValue(max) : '';
      if (minDisplay) minDisplay.textContent = renderMoney(min);
      if (maxDisplay) maxDisplay.textContent = renderMoney(max);

      const span = Math.max(ceiling - floor, 1);
      const minPercent = ((min - floor) / span) * 100;
      const maxPercent = ((max - floor) / span) * 100;
      slider.style.setProperty('--price-range-min', `${minPercent}%`);
      slider.style.setProperty('--price-range-max', `${maxPercent}%`);
    };

    sync();
    [minRange, maxRange].forEach((input) => {
      input.addEventListener('input', () => sync(input));
      input.addEventListener('change', () => sync(input));
    });
  }

  openPanel(selector) {
    document.querySelector('.overlay')?.classList.add('open');
    document.querySelector(selector)?.classList.add('open');
    document.documentElement.classList.add('panel-lock');
    document.body.classList.add('panel-lock');
  }

  async openCartPanel() {
    this.openPanel('.cart-panel');
    const panel = document.querySelector('.cart-panel');
    panel?.classList.add('is-loading');
    try {
      await this.refreshCart();
    } finally {
      panel?.classList.remove('is-loading');
    }
  }

  closePanels() {
    document.querySelector('.overlay')?.classList.remove('open');
    document.querySelectorAll('.search-panel, .cart-panel').forEach((panel) => panel.classList.remove('open'));
    document.querySelectorAll('.collection-layout.filters-open').forEach((layout) => {
      layout.classList.remove('filters-open');
      layout.querySelector('[data-filter-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.remove('filter-lock');
    document.body.classList.remove('filter-lock');
    document.documentElement.classList.remove('panel-lock');
    document.body.classList.remove('panel-lock');
  }

  setupProductForms() {
    document.querySelectorAll('form.product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        const submitter = event.submitter;
        if (submitter && !submitter.matches('[data-add-to-cart]')) return;
        event.preventDefault();
        if (form.dataset.submitting === 'true') return;
        const button = form.querySelector('[data-add-to-cart]');
        const label = button?.querySelector('span');
        const error = form.querySelector('.product-form__error-message-wrapper');
        form.dataset.submitting = 'true';
        button?.setAttribute('disabled', 'disabled');
        if (label) label.textContent = this.strings.adding || 'Adding...';
        try {
          const response = await this.fetchWithTimeout(this.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
            cache: 'no-store'
          }, 12000);
          if (!response.ok) throw await response.json();
          const addedItem = await response.json();
          this.trackPinterestAddToCart(addedItem);
          if (error) error.hidden = true;
          this.openCartPanel();
        } catch (err) {
          const message = err.description || err.errors || err.message || this.strings.cartError || 'Cart error';
          if (error) {
            error.hidden = false;
            error.querySelector('.product-form__error-message').textContent = message;
          }
        } finally {
          form.dataset.submitting = 'false';
          this.restoreProductSubmitButton(form);
        }
      });
    });
  }

  setupNewsletterForms() {
    document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
      if (form.dataset.newsletterBound === 'true') return;
      form.dataset.newsletterBound = 'true';
      form.addEventListener('submit', () => {
        const button = form.querySelector('[data-newsletter-submit], button[type="submit"]');
        if (!button || button.disabled) return;
        button.dataset.originalText = button.textContent.trim();
        button.textContent = this.strings.newsletterLoading || 'Joining...';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        form.classList.add('is-submitting');
      });
    });
  }

  restoreProductSubmitButton(form) {
    const button = form?.querySelector('[data-add-to-cart]');
    const label = button?.querySelector('span');
    const variantId = form?.querySelector('[data-variant-id]')?.value;
    const picker = document.querySelector(`variant-radios[data-section="${this.productFormSectionId(form)}"]`);
    const variant = picker?.variantData?.find((item) => String(item.id) === String(variantId));
    const available = variant ? Boolean(variant.available) : true;
    if (button) button.disabled = !available;
    if (label) label.textContent = available ? (this.strings.addToCart || 'Add to bag') : (this.strings.soldOut || 'Sold out');
  }

  productFormSectionId(form) {
    return String(form?.getAttribute('id') || '').replace('product-form-', '');
  }

  trackPinterestAddToCart(item) {
    if (typeof window.oppcolorPinterestTrack !== 'function' || !item) return;
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const cents = Number(
      item.final_line_price
      ?? item.original_line_price
      ?? (Number(item.final_price ?? item.price ?? 0) * quantity)
    );
    const value = Number.isFinite(cents) ? Number((cents / 100).toFixed(2)) : 0;

    window.oppcolorPinterestTrack('addtocart', {
      value,
      order_quantity: quantity,
      currency: item.currency || window.themeCurrency || 'USD'
    });
  }

  fetchWithTimeout(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => {
      window.clearTimeout(timer);
    });
  }

  setupVariantPickers() {
    document.querySelectorAll('variant-radios').forEach((picker) => {
      const variants = JSON.parse(picker.querySelector('script[type=\"application/json\"]')?.textContent || '[]');
      picker.variantData = variants;
      this.syncVariantAvailability(picker);
      this.updateSelectedVariant(picker);
      picker.addEventListener('change', () => this.updateSelectedVariant(picker));
    });
  }

  syncVariantAvailability(picker) {
    const productUrl = picker.dataset.url;
    if (!productUrl) return;
    fetch(`${productUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((product) => {
        if (!product?.variants?.length) return;
        const liveVariants = new Map(product.variants.map((variant) => [String(variant.id), variant]));
        picker.variantData = picker.variantData.map((variant) => {
          const liveVariant = liveVariants.get(String(variant.id));
          return liveVariant ? { ...variant, ...liveVariant, available: Boolean(liveVariant.available) } : variant;
        });
        this.updateOptionAvailability(picker);
        this.updateSelectedVariant(picker);
      })
      .catch(() => {});
  }

  updateOptionAvailability(picker) {
    picker.querySelectorAll('input[type=\"radio\"]').forEach((input) => {
      const optionIndex = Number(input.id.split('-').slice(-2, -1)[0]) - 1;
      const hasAvailableVariant = picker.variantData?.some((variant) => variant.available && variant.options?.[optionIndex] === input.value);
      input.classList.toggle('disabled', !hasAvailableVariant);
    });
  }

  updateSelectedVariant(picker) {
    const selected = [...picker.querySelectorAll('input[type=\"radio\"]:checked')].map((input) => input.value);
    const variant = picker.variantData?.find((item) => item.options.every((option, index) => option === selected[index]));
    const form = document.getElementById(`product-form-${picker.dataset.section}`);
    const idInput = form?.querySelector('[data-variant-id]');
    const button = form?.querySelector('[data-add-to-cart]');
    const label = button?.querySelector('span');
    if (!variant) {
      button?.setAttribute('disabled', 'disabled');
      if (label) label.textContent = this.strings.unavailable || 'Unavailable';
      this.syncProductQuickAdd(picker, null);
      return;
    }
    if (idInput) idInput.value = variant.id;
    if (button) button.disabled = !variant.available;
    if (label) label.textContent = variant.available ? (this.strings.addToCart || 'Add to bag') : (this.strings.soldOut || 'Sold out');
    const priceNode = document.querySelector('[data-price]');
    if (priceNode && window.Shopify?.formatMoney) this.renderProductPrice(priceNode, variant);
    if (variant.featured_image) this.updateProductStage(variant.featured_image.src, '', variant.featured_image.alt || '');
    this.syncProductQuickAdd(picker, variant);
  }

  setupProductQuickAdd() {
    document.querySelectorAll('[data-product-quick-add]').forEach((quickAdd) => {
      if (quickAdd.dataset.quickAddBound === 'true') return;
      quickAdd.dataset.quickAddBound = 'true';
      const sectionId = quickAdd.dataset.productSectionId;
      const productSection = document.querySelector(`[data-product-section-id="${sectionId}"]`) || document.querySelector('[data-product-section]');
      const form = document.getElementById(`product-form-${sectionId}`);
      const picker = document.querySelector(`variant-radios[data-section="${sectionId}"]`);
      const trigger = quickAdd.dataset.trigger === 'options' && picker ? picker : form;
      if (!productSection || !form || !trigger) return;
      let quickAddVisibilityFrame = null;

      const syncVisibility = () => {
        quickAddVisibilityFrame = null;
        const hiddenByOverlay = document.documentElement.classList.contains('panel-lock')
          || document.documentElement.classList.contains('popup-lock')
          || document.documentElement.classList.contains('size-chart-lock')
          || document.documentElement.classList.contains('filter-lock');
        const mobileDisabled = quickAdd.classList.contains('product-quick-add--mobile-disabled') && window.matchMedia('(max-width: 760px)').matches;
        const rect = trigger.getBoundingClientRect();
        const hasScrolledPastTrigger = rect.bottom <= 0;
        const shouldShow = hasScrolledPastTrigger && !hiddenByOverlay && !mobileDisabled;
        quickAdd.classList.toggle('is-visible', shouldShow);
        quickAdd.setAttribute('aria-hidden', String(!shouldShow));
        document.body.classList.toggle('quick-add-visible', shouldShow);
      };
      const requestVisibilitySync = () => {
        if (quickAddVisibilityFrame) return;
        quickAddVisibilityFrame = window.requestAnimationFrame(syncVisibility);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(requestVisibilitySync, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
        observer.observe(trigger);
      }
      syncVisibility();
      window.addEventListener('scroll', requestVisibilitySync, { passive: true });
      window.addEventListener('resize', requestVisibilitySync);

      quickAdd.querySelectorAll('[data-quick-add-option]').forEach((select) => {
        select.addEventListener('change', () => {
          if (!picker) return;
          const position = Number(select.dataset.optionPosition);
          const value = select.value;
          const input = [...picker.querySelectorAll('input[type="radio"]')]
            .find((radio) => radio.id.startsWith(`${sectionId}-${position}-`) && radio.value === value);
          if (!input || input.disabled || input.classList.contains('disabled')) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      quickAdd.querySelector('[data-quick-add-submit]')?.addEventListener('click', () => {
        const addButton = form.querySelector('[data-add-to-cart]');
        if (!addButton || addButton.disabled) {
          const firstSelect = quickAdd.querySelector('[data-quick-add-option]');
          if (firstSelect) {
            firstSelect.focus();
            return;
          }
          (picker || form).scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        addButton.click();
      });

      this.syncProductQuickAdd(picker, this.currentProductVariant(sectionId));
    });
  }

  currentProductVariant(sectionId) {
    const picker = document.querySelector(`variant-radios[data-section="${sectionId}"]`);
    const form = document.getElementById(`product-form-${sectionId}`);
    const variantId = form?.querySelector('[data-variant-id]')?.value;
    if (picker?.variantData?.length) return picker.variantData.find((variant) => String(variant.id) === String(variantId));
    return variantId ? { id: variantId, available: !form?.querySelector('[data-add-to-cart]')?.disabled } : null;
  }

  syncProductQuickAdd(picker, variant) {
    const sectionId = picker?.dataset.section || document.querySelector('[data-product-quick-add]')?.dataset.productSectionId;
    const quickAdd = document.querySelector(`[data-product-quick-add][data-product-section-id="${sectionId}"]`);
    if (!quickAdd) return;
    const form = document.getElementById(`product-form-${sectionId}`);
    const button = quickAdd.querySelector('[data-quick-add-submit]');
    const label = quickAdd.querySelector('[data-quick-add-label]');
    const price = quickAdd.querySelector('[data-quick-add-price]');
    const options = quickAdd.querySelector('[data-quick-add-options]');
    const media = quickAdd.querySelector('[data-quick-add-media]');
    const sourceButton = form?.querySelector('[data-add-to-cart]');
    const available = variant ? Boolean(variant.available) : false;
    const selectedOptions = variant?.title && variant.title !== 'Default Title' ? variant.title : quickAdd.dataset.readyText;

    if (button) button.disabled = !available;
    if (label) {
      label.textContent = !variant
        ? (quickAdd.dataset.selectLabel || this.strings.unavailable || 'Select options')
        : available
          ? (quickAdd.dataset.buttonLabel || this.strings.addToCart || 'Add to cart')
          : (this.strings.soldOut || 'Sold out');
    }
    if (price && variant?.price != null) price.textContent = this.money(variant.price);
    if (options) options.textContent = selectedOptions || '';
    this.syncProductQuickAddOptions(quickAdd, picker);
    if (media && variant?.featured_image?.src) {
      const image = media.querySelector('img');
      if (image) {
        image.src = variant.featured_image.src;
        image.alt = variant.featured_image.alt || '';
      }
    }
    quickAdd.classList.toggle('is-unavailable', !available);
    if (sourceButton?.disabled && button) button.disabled = true;
  }

  syncProductQuickAddOptions(quickAdd, picker) {
    if (!quickAdd || !picker) return;
    quickAdd.querySelectorAll('[data-quick-add-option]').forEach((select) => {
      const position = Number(select.dataset.optionPosition);
      select.querySelectorAll('option').forEach((option) => {
        const sourceInput = [...picker.querySelectorAll('input[type="radio"]')]
          .find((radio) => radio.id.startsWith(`${picker.dataset.section}-${position}-`) && radio.value === option.value);
        option.disabled = Boolean(sourceInput?.disabled || sourceInput?.classList.contains('disabled'));
        if (sourceInput?.checked) select.value = option.value;
      });
    });
  }

  setupProductGallery() {
    document.querySelectorAll('.product-thumbs').forEach((track) => {
      this.setupThumbDragScroll(track);
      if (track.dataset.clickBound === 'true') return;
      track.dataset.clickBound = 'true';
      track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-product-thumb]');
        if (!button || !track.contains(button)) return;
        if (track?.dataset.dragMoved === 'true') {
          event.preventDefault();
          return;
        }
        this.selectProductThumb(button);
      });
      const activeThumb = track.querySelector('[data-product-thumb].active') || track.querySelector('[data-product-thumb]');
      this.preloadAdjacentProductThumbs(activeThumb);
      this.preloadProductGalleryPreviews(track);
    });
    document.querySelectorAll('[data-product-gallery-prev], [data-product-gallery-next]').forEach((button) => {
      if (button.dataset.galleryNavBound === 'true') return;
      button.dataset.galleryNavBound = 'true';
      button.addEventListener('click', () => {
        const stage = button.closest('.product-stage');
        const thumbs = [...stage?.querySelectorAll('[data-product-thumb]') || []];
        if (!thumbs.length) return;
        const currentIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('active')));
        const direction = button.matches('[data-product-gallery-prev]') ? -1 : 1;
        const nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;
        this.selectProductThumb(thumbs[nextIndex], { slideDirection: direction });
      });
    });
  }

  selectProductThumb(button, options = {}) {
    if (!button) return;
    let transition = Promise.resolve();
    if (options.slideDirection) {
      transition = this.slideProductStage(button.dataset.previewSrc || button.dataset.fullSrc, '', button.dataset.alt || '', options.slideDirection);
    } else {
      this.updateProductStage(button.dataset.previewSrc || button.dataset.fullSrc, '', button.dataset.alt || '', true);
    }
    Promise.all([transition, this.preloadImage(button.dataset.fullSrc, button.dataset.srcset)]).then(() => {
      if (button.classList.contains('active')) this.updateProductStage(button.dataset.fullSrc, button.dataset.srcset, button.dataset.alt || '');
    });
    button.closest('.product-thumbs')?.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      const active = thumb === button;
      thumb.classList.toggle('active', active);
      thumb.setAttribute('aria-current', String(active));
    });
    requestAnimationFrame(() => this.scrollProductThumbIntoView(button));
    this.preloadAdjacentProductThumbs(button);
  }

  preloadAdjacentProductThumbs(button) {
    const track = button?.closest('.product-thumbs');
    if (!track) return;
    const thumbs = [...track.querySelectorAll('[data-product-thumb]')];
    const index = thumbs.indexOf(button);
    if (index < 0) return;
    [-2, -1, 0, 1, 2].forEach((offset) => {
      const thumb = thumbs[(index + offset + thumbs.length) % thumbs.length];
      this.preloadImage(thumb?.dataset.previewSrc);
    });
    [-1, 0, 1].forEach((offset) => {
      const thumb = thumbs[(index + offset + thumbs.length) % thumbs.length];
      this.preloadImage(thumb?.dataset.fullSrc, thumb?.dataset.srcset);
    });
  }

  preloadProductGalleryPreviews(track) {
    if (!track || track.dataset.previewPreloadBound === 'true') return;
    track.dataset.previewPreloadBound = 'true';
    const thumbs = [...track.querySelectorAll('[data-product-thumb]')];
    const run = () => {
      thumbs.forEach((thumb, index) => {
        window.setTimeout(() => this.preloadImage(thumb.dataset.previewSrc), index * 90);
      });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1400 });
    } else {
      window.setTimeout(run, 400);
    }
  }

  slideProductStage(src, srcset, alt, direction = 1) {
    const stage = document.querySelector('[data-product-stage]');
    if (!stage || !src) return Promise.resolve();
    const wrapper = stage.closest('.product-stage');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !stage.animate) {
      this.updateProductStage(src, srcset, alt, true);
      return Promise.resolve();
    }

    const token = String(Date.now());
    stage.dataset.slideToken = token;
    stage.getAnimations().forEach((animation) => animation.cancel());
    wrapper?.querySelectorAll('.product-stage-slide-image').forEach((image) => image.remove());
    wrapper?.classList.add('is-sliding');
    const distance = Math.min(stage.getBoundingClientRect().width * 0.16, 72);

    return this.preloadImage(src).then(() => {
      if (stage.dataset.slideToken !== token) return;
      const incoming = document.createElement('img');
      incoming.className = 'product-stage-slide-image';
      incoming.alt = alt || stage.alt || '';
      incoming.src = src;
      wrapper?.appendChild(incoming);

      const outgoingAnimation = stage.animate([
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0.22, transform: `translateX(${-direction * distance}px)` }
      ], {
        duration: 320,
        easing: 'cubic-bezier(.16, 1, .3, 1)',
        fill: 'forwards'
      });
      const incomingAnimation = incoming.animate([
        { opacity: 0, transform: `translateX(${direction * distance}px)` },
        { opacity: 1, transform: 'translateX(0)' }
      ], {
        duration: 360,
        easing: 'cubic-bezier(.16, 1, .3, 1)',
        fill: 'forwards'
      });

      return Promise.all([outgoingAnimation.finished, incomingAnimation.finished]).then(() => {
        if (stage.dataset.slideToken !== token) return;
        incoming.remove();
        stage.getAnimations().forEach((animation) => animation.cancel());
        stage.style.opacity = '';
        stage.style.transform = '';
        wrapper?.classList.remove('is-sliding');
        delete stage.dataset.slideToken;
        this.updateProductStage(src, srcset, alt, false);
      });
    }).finally(() => {
      if (stage.dataset.slideToken !== token) return;
      wrapper?.classList.remove('is-sliding');
      stage.style.opacity = '';
      stage.style.transform = '';
      wrapper?.querySelectorAll('.product-stage-slide-image').forEach((image) => image.remove());
      delete stage.dataset.slideToken;
    }).catch(() => {
      wrapper?.classList.remove('is-sliding');
      wrapper?.querySelectorAll('.product-stage-slide-image').forEach((image) => image.remove());
    });
  }

  setupThumbDragScroll(track) {
    if (track.dataset.dragBound === 'true') return;
    track.dataset.dragBound = 'true';
    track.querySelectorAll('img, button').forEach((node) => {
      node.setAttribute('draggable', 'false');
    });
    track.addEventListener('dragstart', (event) => event.preventDefault());
    let active = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let pointerId = null;

    const startDrag = (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== undefined && event.button !== 0) return;
      active = true;
      moved = false;
      pointerId = event.pointerId ?? null;
      track.dataset.dragMoved = 'false';
      startX = event.clientX;
      startScroll = track.scrollLeft;
    };

    const moveDrag = (event) => {
      if (!active) return;
      if (pointerId !== null && event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) {
        moved = true;
        track.dataset.dragMoved = 'true';
        track.classList.add('is-dragging');
        event.preventDefault();
      }
      track.scrollLeft = startScroll - delta;
    };

    const endDrag = (event) => {
      if (!active) return;
      if (pointerId !== null && event.pointerId !== pointerId) return;
      active = false;
      pointerId = null;
      track.classList.remove('is-dragging');
      track.dataset.dragMoved = String(moved);
      window.setTimeout(() => {
        track.dataset.dragMoved = 'false';
      }, 80);
    };

    if (window.PointerEvent) {
      track.addEventListener('pointerdown', startDrag, true);
      window.addEventListener('pointermove', moveDrag, { capture: true, passive: false });
      window.addEventListener('pointerup', endDrag, true);
      window.addEventListener('pointercancel', endDrag, true);
    } else {
      track.addEventListener('mousedown', startDrag, true);
      window.addEventListener('mousemove', moveDrag, { capture: true, passive: false });
      window.addEventListener('mouseup', endDrag, true);
    }
  }

  scrollProductThumbIntoView(button) {
    const track = button.closest('.product-thumbs');
    if (!track) return;
    const trackRect = track.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const styles = getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 8;
    const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
    const comfortableEdge = buttonRect.width + gap * 2;
    const visibleLeft = trackRect.left + paddingLeft + comfortableEdge;
    const visibleRight = trackRect.right - paddingRight - comfortableEdge;
    let nextScroll = track.scrollLeft;

    if (buttonRect.right > visibleRight) {
      nextScroll += buttonRect.left + buttonRect.width / 2 - (trackRect.left + trackRect.width / 2);
    } else if (buttonRect.left < visibleLeft) {
      nextScroll += buttonRect.left + buttonRect.width / 2 - (trackRect.left + trackRect.width / 2);
    } else {
      return;
    }

    track.scrollTo({
      left: Math.max(0, Math.min(nextScroll, track.scrollWidth - track.clientWidth)),
      behavior: 'smooth'
    });
  }

  preloadImage(src, srcset = '') {
    if (!src) return Promise.resolve();
    if (!this.preloadedImages) this.preloadedImages = new Map();
    const key = `${src}|${srcset}`;
    if (this.preloadedImages.has(key)) return this.preloadedImages.get(key);
    const promise = new Promise((resolve) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = resolve;
      if (srcset) image.srcset = srcset;
      image.src = src;
      if (image.decode) image.decode().then(resolve).catch(resolve);
    });
    this.preloadedImages.set(key, promise);
    return promise;
  }

  updateProductStage(src, srcset, alt, preview = false) {
    const stage = document.querySelector('[data-product-stage]');
    if (!stage || !src) return;
    const wrapper = stage.closest('.product-stage');
    wrapper?.classList.toggle('is-loading', preview);
    stage.src = src;
    if (srcset) {
      stage.srcset = srcset;
    } else {
      stage.removeAttribute('srcset');
    }
    stage.alt = alt || stage.alt;
    if (!preview) wrapper?.classList.remove('is-loading');
  }

  setupRelatedProducts() {
    document.querySelectorAll('[data-related-products]').forEach((section) => {
      if (section.dataset.relatedProductsBound === 'true') return;
      section.dataset.relatedProductsBound = 'true';
      if (section.dataset.recommendationsPerformed === 'true') return;

      const productId = section.dataset.productId;
      const sectionId = section.dataset.sectionId;
      const limit = section.dataset.limit || 4;
      const recommendationsUrl = this.routes.product_recommendations_url || '/recommendations/products';
      if (!productId || !sectionId) return;

      const url = `${recommendationsUrl}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=related`;
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => response.ok ? response.text() : '')
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextSection = doc.querySelector('[data-related-products]');
          const count = Number(nextSection?.dataset.recommendationsCount || 0);
          const nextGrid = nextSection?.querySelector('[data-related-products-grid]');
          const currentGrid = section.querySelector('[data-related-products-grid]');
          if (count > 0 && nextGrid && currentGrid) {
            currentGrid.replaceWith(nextGrid);
            section.dataset.recommendationsPerformed = 'true';
            section.dataset.recommendationsCount = String(count);
          }
        })
        .catch(() => {});
    });
  }

  renderProductPrice(node, variant) {
    const sale = Shopify.formatMoney(variant.price);
    const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price ? Shopify.formatMoney(variant.compare_at_price) : '';
    const showSaleBadge = node.dataset.showSaleBadge === 'true';
    const discountBadge = showSaleBadge && compareAt ? `<span class="price-sale-badge">${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}% off</span>` : '';
    node.classList.toggle('price--on-sale', Boolean(compareAt));
    node.innerHTML = `<span class="price-sale">${sale}</span>${compareAt ? `<s class="price-compare">${compareAt}</s>` : ''}${discountBadge}`;
  }

  setupSizeChart() {
    const modal = document.querySelector('[data-size-chart-modal]');
    if (!modal) return;
    const open = () => {
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('size-chart-lock');
      document.body.classList.add('size-chart-lock');
    };
    const close = () => {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('size-chart-lock');
      document.body.classList.remove('size-chart-lock');
    };
    document.querySelectorAll('[data-open-size-chart]').forEach((button) => {
      button.addEventListener('click', open);
    });
    modal.querySelector('[data-close-size-chart]')?.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('open')) close();
    });
  }

  setupBackToTop() {
    document.querySelectorAll('[data-back-to-top]').forEach((button) => {
      const sync = () => {
        button.classList.toggle('is-visible', window.scrollY > 520);
      };
      sync();
      window.addEventListener('scroll', sync, { passive: true });
      button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  setupHeaderScrollState() {
    const sync = () => {
      document.body.classList.toggle('is-scrolled', window.scrollY > 6);
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true });
  }

  setupThemeEditor() {
    if (!window.Shopify?.designMode) return;
    const setOffset = () => {
      const nav = document.querySelector('.nav');
      const offset = nav ? Math.ceil(nav.getBoundingClientRect().height) + 18 : 96;
      document.documentElement.style.setProperty('--theme-editor-scroll-offset', `${offset}px`);
    };
    const scrollToSection = (sectionId) => {
      const section = document.getElementById(`shopify-section-${sectionId}`);
      if (!section) return;
      setOffset();
      const offset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--theme-editor-scroll-offset')) || 96;
      const top = section.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    };

    setOffset();
    window.addEventListener('resize', setOffset);
    document.addEventListener('shopify:section:select', (event) => {
      window.requestAnimationFrame(() => scrollToSection(event.detail.sectionId));
    });
    document.addEventListener('shopify:section:load', () => {
      window.requestAnimationFrame(() => this.setupYoutubeVideos());
    });
    document.addEventListener('shopify:block:select', (event) => {
      window.requestAnimationFrame(() => {
        event.target?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      });
    });
  }

  setupYoutubeVideos() {
    document.querySelectorAll('[data-youtube-video-section]').forEach((section) => {
      if (section.dataset.youtubeVideoBound === 'true') return;
      section.dataset.youtubeVideoBound = 'true';

      const grid = section.querySelector('[data-youtube-video-grid]');
      const modal = section.querySelector('[data-youtube-video-modal]');
      const iframe = section.querySelector('[data-youtube-video-iframe]');
      const closeButtons = section.querySelectorAll('[data-youtube-video-close]');
      const prevButton = section.querySelector('[data-youtube-video-prev]');
      const nextButton = section.querySelector('[data-youtube-video-next]');
      const empty = section.querySelector('[data-youtube-video-empty]');
      const loading = section.querySelector('[data-youtube-video-loading]');
      const actions = section.querySelector('[data-youtube-video-actions]');
      const initialCount = Number(grid?.dataset.initialCount || 8);
      const loadStep = Number(grid?.dataset.loadStep || 8);
      let activeVideoIndex = -1;

      const close = () => {
        modal?.classList.remove('open');
        modal?.classList.remove('is-portrait', 'is-landscape');
        modal?.setAttribute('aria-hidden', 'true');
        if (iframe) iframe.src = 'about:blank';
        activeVideoIndex = -1;
        document.documentElement.classList.remove('youtube-video-lock');
        document.body.classList.remove('youtube-video-lock');
      };

      const videoOpeners = () => [...section.querySelectorAll('[data-youtube-video-open]')];

      const syncVideoNav = () => {
        const count = videoOpeners().length;
        const disabled = count < 2;
        prevButton?.toggleAttribute('disabled', disabled);
        nextButton?.toggleAttribute('disabled', disabled);
      };

      const openVideoAt = (index) => {
        const openers = videoOpeners();
        if (!openers.length || !modal || !iframe) return;
        activeVideoIndex = (index + openers.length) % openers.length;
        const opener = openers[activeVideoIndex];
        const videoId = opener.dataset.videoId;
        if (!videoId) return;

        const image = opener.querySelector('img');
        const orientation = opener.dataset.orientation || '';
        const isPortrait = orientation === 'portrait' || (!orientation && image && image.naturalHeight > image.naturalWidth);
        const playerParams = new URLSearchParams({
          autoplay: '1',
          mute: '1',
          rel: '0',
          fs: '0',
          showinfo: '0',
          controls: '0',
          enablejsapi: '1',
        });
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${playerParams.toString()}`;
        modal.classList.toggle('is-portrait', Boolean(isPortrait));
        modal.classList.toggle('is-landscape', !isPortrait);
        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('youtube-video-lock');
        document.body.classList.add('youtube-video-lock');
        syncVideoNav();
      };

      const syncLoadMore = () => {
        const cards = [...section.querySelectorAll('[data-youtube-video-card]')];
        const loadMore = section.querySelector('[data-youtube-video-load-more]');
        const hasHidden = cards.some((card) => card.classList.contains('is-hidden'));
        if (actions) actions.hidden = !hasHidden;
        if (loadMore) loadMore.hidden = !hasHidden;
      };

      const showNextCards = () => {
        const hiddenCards = [...section.querySelectorAll('[data-youtube-video-card].is-hidden')].slice(0, loadStep);
        hiddenCards.forEach((card) => card.classList.remove('is-hidden'));
        syncLoadMore();
      };

      section.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-youtube-video-open]');
        if (opener && section.contains(opener)) {
          openVideoAt(videoOpeners().indexOf(opener));
          return;
        }

        const loadMore = event.target.closest('[data-youtube-video-load-more]');
        if (loadMore && section.contains(loadMore)) showNextCards();
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', close);
      });

      prevButton?.addEventListener('click', () => {
        if (activeVideoIndex >= 0) openVideoAt(activeVideoIndex - 1);
      });

      nextButton?.addEventListener('click', () => {
        if (activeVideoIndex >= 0) openVideoAt(activeVideoIndex + 1);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal?.classList.contains('open')) close();
        if (event.key === 'ArrowLeft' && modal?.classList.contains('open')) openVideoAt(activeVideoIndex - 1);
        if (event.key === 'ArrowRight' && modal?.classList.contains('open')) openVideoAt(activeVideoIndex + 1);
      });

      if (grid?.dataset.feedUrl) {
        if (actions) actions.hidden = true;
        section.querySelector('[data-youtube-video-load-more]')?.setAttribute('hidden', '');
        this.loadYoutubeVideoFeed(grid, { empty, loading, initialCount, syncLoadMore });
      } else {
        syncLoadMore();
      }
    });
  }

  async loadYoutubeVideoFeed(grid, options) {
    const { empty, loading, initialCount, syncLoadMore } = options;
    const url = new URL(grid.dataset.feedUrl, window.location.href);
    const limit = Number(grid.dataset.limit || 48);
    const category = grid.dataset.category || '';

    url.searchParams.set('limit', String(limit));
    if (category) url.searchParams.set('category', category);

    grid.setAttribute('aria-busy', 'true');
    if (loading) loading.hidden = false;
    if (empty) empty.hidden = true;
    try {
      const response = await fetch(url.href, {
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Video feed request failed');
      const payload = await response.json();
      const videos = Array.isArray(payload.videos) ? payload.videos : [];
      grid.innerHTML = videos.map((video, index) => this.youtubeVideoCard(video, index >= initialCount)).join('');
      if (empty) {
        empty.hidden = videos.length > 0;
        empty.textContent = videos.length > 0 ? '' : (grid.dataset.emptyText || 'No videos are available yet.');
      }
      syncLoadMore();
    } catch (_) {
      grid.innerHTML = '';
      if (empty) {
        empty.hidden = false;
        empty.textContent = grid.dataset.emptyText || 'No videos are available yet.';
      }
      syncLoadMore();
    } finally {
      grid.removeAttribute('aria-busy');
      if (loading) loading.hidden = true;
    }
  }

  youtubeVideoCard(video, hidden = false) {
    const videoId = this.escape(video.video_id || '');
    const title = this.escape(this.decodeHtmlEntities(video.title || ''));
    const thumbnail = this.escape(video.thumbnail_url || '');
    const orientation = video.orientation === 'portrait' ? 'portrait' : 'landscape';
    const published = video.published_at ? new Date(video.published_at) : null;
    const dateText = published && !Number.isNaN(published.getTime())
      ? published.toLocaleDateString(document.documentElement.lang || 'en', { year: 'numeric', month: 'short', day: '2-digit' })
      : '';
    const dateIso = published && !Number.isNaN(published.getTime()) ? published.toISOString() : '';

    return `
      <article class="youtube-video-card${hidden ? ' is-hidden' : ''}" data-youtube-video-card>
        <button class="youtube-video-thumb" type="button" data-youtube-video-open data-video-id="${videoId}" data-orientation="${orientation}" aria-label="Play ${title}">
          ${thumbnail ? `<img src="${thumbnail}" width="1280" height="720" loading="lazy" alt="${title}">` : '<span class="youtube-video-thumb-placeholder"></span>'}
          <span class="youtube-play-mark" aria-hidden="true"></span>
        </button>
        <div class="youtube-video-meta">
          <h3>${title}</h3>
          ${dateText ? `<time datetime="${dateIso}">${this.escape(dateText)}</time>` : ''}
        </div>
      </article>
    `;
  }

  async refreshCart() {
    try {
      const url = new URL(`${this.routes.cart_url}.js`, window.location.origin);
      url.searchParams.set('_', Date.now());
      const response = await fetch(url.href, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();
      await this.renderCart(cart);
    } catch (_) {}
  }

  async changeCart(key, quantity) {
    const panel = document.querySelector('.cart-panel');
    panel?.classList.add('is-loading');
    try {
      const response = await fetch(this.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw await response.json();
      const cart = await response.json();
      await this.renderCart(cart);
    } catch (err) {
      const errors = document.querySelector('[data-cart-errors]');
      if (errors) errors.textContent = err.description || this.strings.cartError || 'Cart error';
    } finally {
      panel?.classList.remove('is-loading');
    }
  }

  async renderCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    const currency = cart.currency || window.Shopify?.currency?.active || 'USD';
    document.querySelectorAll('[data-cart-total]').forEach((node) => { node.textContent = this.money(cart.total_price, currency); });
    document.querySelectorAll('[data-cart-footer]').forEach((node) => { node.hidden = !cart.items.length; });
    document.querySelectorAll('[data-cart-view-link]').forEach((node) => { node.hidden = !cart.items.length; });
    const list = document.querySelector('[data-cart-items]');
    if (!list) return;
    if (!cart.items.length) {
      list.innerHTML = `<div class=\"search-empty visible\"><div><span class=\"label\">Bag</span><h3>Your cart is quiet.</h3><p class=\"body-copy\">Add a pair from the collection to begin a fitting request.</p><div class=\"cart-empty-actions\"><a class=\"button secondary\" href=\"/collections/all\">Continue shopping</a></div></div></div>`;
      document.querySelector('.cart-footer button[name=\"checkout\"]')?.setAttribute('disabled', 'disabled');
      return;
    }
    document.querySelector('.cart-footer button[name=\"checkout\"]')?.removeAttribute('disabled');
    const compareAtPrices = await this.cartCompareAtPrices(cart.items);
    list.innerHTML = cart.items.map((item) => `
      <div class=\"cart-line\" data-cart-line=\"${item.key}\">
        ${item.image ? `<img src="${item.image}" width="74" height="86" alt="${this.escape(item.product_title)}">` : ''}
        <span class=\"cart-info\"><strong><a href=\"${item.url}\">${this.escape(item.product_title)}</a></strong><small>${this.escape(item.variant_title || '')}</small>
          <span class=\"cart-line-subtotal\"><span>Qty ${item.quantity}</span><span class=\"cart-line-price\">${this.cartCompareAtMarkup(item, compareAtPrices, currency)}<strong>${this.money(item.final_line_price, currency)}</strong></span></span>
          <span class=\"quantity-control\"><button type=\"button\" data-cart-key=\"${item.key}\" data-cart-quantity=\"${item.quantity - 1}\"${item.quantity <= 1 ? ' disabled' : ''}>-</button><span>${item.quantity}</span><button type=\"button\" data-cart-key=\"${item.key}\" data-cart-quantity=\"${item.quantity + 1}\">+</button></span>
        </span>
        <button class=\"cart-line-remove\" type=\"button\" data-cart-key=\"${item.key}\" data-cart-quantity=\"0\" aria-label=\"Remove ${this.escape(item.product_title)}\">remove</button>
      </div>`).join('');
  }

  async cartCompareAtPrices(items) {
    if (!this.productJsonCache) this.productJsonCache = new Map();
    const entries = await Promise.all(items.map(async (item) => {
      const productPath = String(item.url || '').split('?')[0];
      if (!productPath) return [item.variant_id, 0];
      const productUrl = `${productPath}.js`;
      try {
        if (!this.productJsonCache.has(productUrl)) {
          this.productJsonCache.set(productUrl, fetch(productUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json()));
        }
        const product = await this.productJsonCache.get(productUrl);
        const variant = product.variants?.find((candidate) => Number(candidate.id) === Number(item.variant_id));
        return [item.variant_id, Number(variant?.compare_at_price || 0)];
      } catch (_) {
        return [item.variant_id, 0];
      }
    }));
    return new Map(entries);
  }

  cartCompareAtMarkup(item, compareAtPrices, currency) {
    const compareAt = Number(compareAtPrices.get(item.variant_id) || 0);
    const compareLinePrice = compareAt > Number(item.final_price || item.price || 0)
      ? compareAt * Number(item.quantity || 1)
      : Number(item.original_line_price || 0);
    return compareLinePrice > Number(item.final_line_price || 0) ? `<s>${this.money(compareLinePrice, currency)}</s>` : '';
  }

  async predictiveSearch(term) {
    const results = document.querySelector('[data-search-results]');
    const empty = document.querySelector('[data-search-empty]');
    if (!results) return;
    if (!term.trim()) {
      results.innerHTML = '';
      empty?.classList.remove('visible');
      return;
    }
    try {
      const url = `${this.routes.predictive_search_url}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      const html = await fetch(url).then((response) => response.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = [...doc.querySelectorAll('[data-predictive-search-result]')];
      results.innerHTML = items.map((item) => item.innerHTML).join('');
      empty?.classList.toggle('visible', items.length === 0);
    } catch (_) {
      results.innerHTML = `<a class=\"search-result\" href=\"${this.routes.search_url}?q=${encodeURIComponent(term)}\"><span><strong>Search for “${this.escape(term)}”</strong></span></a>`;
    }
  }

  setupPopup() {
    const popup = document.querySelector('[data-marketing-popup]');
    if (!popup || localStorage.getItem('oppcolorPopupClosed')) return;
    const delay = Number(popup.dataset.popupDelay || 1400);
    const close = () => {
      popup.classList.remove('open');
      popup.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('popup-lock');
      document.body.classList.remove('popup-lock');
      localStorage.setItem('oppcolorPopupClosed', '1');
    };
    setTimeout(() => {
      popup.classList.add('open');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('popup-lock');
      document.body.classList.add('popup-lock');
    }, delay);
    popup.querySelectorAll('[data-close-popup]').forEach((button) => {
      button.addEventListener('click', close);
    });
  }

  money(cents, currency = 'USD') {
    if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  }

  escape(value) {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    };
    return String(value || '').replace(/[&<>'"]/g, (char) => entities[char]);
  }

  decodeHtmlEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  }
}

document.addEventListener('DOMContentLoaded', () => new OppcolorTheme());
